import { useState } from 'react';
import { Dumbbell, ChevronDown, ChevronUp, PlayCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getTahitiDayOfWeek } from '../../utils/tahiti';

const GROUP_COLORS = { 1: '#3b82f6', 2: '#ef4444', 3: '#22c55e', 4: '#f59e0b', 5: '#a855f7' };

function SectionBlock({ section, color }) {
  const isFinisher = section.color === 'red';
  const isWarmup = section.color === 'yellow';
  const sectionColor = isFinisher ? '#ef4444' : isWarmup ? '#f59e0b' : color;
  return (
    <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-md overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#2A2A2E]" style={{ borderLeftColor: sectionColor, borderLeftWidth: '3px' }}>
        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: `${sectionColor}20`, color: sectionColor }}>
          {section.exo_num > 0 ? `EXO ${section.exo_num}` : 'WARM-UP'}
        </span>
        <span className="text-xs font-medium text-white truncate">{section.title}</span>
      </div>
      {section.video && (
        <div className="px-3 pt-2.5">
          <video src={section.video} controls preload="metadata" playsInline
            className="w-full max-h-52 rounded-md border border-zinc-800 bg-black object-contain"
            data-testid={`home-section-video-${section.exo_num}`} />
        </div>
      )}
      <div className="divide-y divide-zinc-800/40">
        {(section.exercises || []).map((ex, i) => (
          <div key={i} className="flex items-center justify-between gap-2 px-3 py-1.5">
            <span className={`text-xs truncate ${ex.is_finisher ? 'text-red-400' : 'text-zinc-300'}`}>{ex.name}</span>
            <span className="text-[10px] font-mono text-zinc-500 flex-shrink-0">{ex.sets}x{ex.reps}{ex.weight && ex.weight !== 'PDC' ? ` · ${ex.weight}` : ex.weight === 'PDC' ? ' · PDC' : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function TodayProgramCard({ myProgram }) {
  const [open, setOpen] = useState(false);
  const todayIdx = getTahitiDayOfWeek(); // 0=Lun..6=Dim
  const plan = (myProgram?.weekly_plan || []).find(p => p.day_of_week === todayIdx);
  if (!plan || !(plan.sections || []).length) return null;

  const color = GROUP_COLORS[plan.group_num] || '#FF6B35';
  const videoCount = (plan.program_video ? 1 : 0) + (plan.sections || []).filter(s => s.video).length;

  return (
    <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-lg overflow-hidden" data-testid="today-program-card">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between p-4 text-left" data-testid="today-program-toggle">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            <Dumbbell className="w-4 h-4" style={{ color }} />
          </div>
          <div>
            <h2 className="text-xl text-white leading-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              Ma séance du jour — {plan.muscle_group}
            </h2>
            <p className="text-[10px] text-zinc-500">
              {plan.sections.length} blocs{plan.total_sets ? ` · ${plan.total_sets}` : ''}{plan.duration ? ` · ${plan.duration}` : ''}
              {videoCount > 0 && <span className="text-[#FF6B35] font-semibold"> · {videoCount} vidéo{videoCount > 1 ? 's' : ''} 🎬</span>}
            </p>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2.5">
          {plan.objective && <p className="text-[10px] text-zinc-400 italic">🎯 {plan.objective}</p>}
          {plan.program_video && (
            <div className="bg-[#0F0F0F] border border-[#2A2A2E] rounded-md p-3" style={{ borderLeftColor: color, borderLeftWidth: '3px' }}>
              <p className="text-[10px] font-bold mb-2 flex items-center gap-1" style={{ color }}>
                <PlayCircle className="w-3 h-3" /> VIDÉO DE LA SÉANCE
              </p>
              <video src={plan.program_video} controls preload="metadata" playsInline
                className="w-full max-h-60 rounded-md border border-zinc-800 bg-black object-contain"
                data-testid="home-program-video" />
            </div>
          )}
          {plan.sections.map(s => <SectionBlock key={s.exo_num} section={s} color={color} />)}
          <Link to="/entrainement" className="block text-center text-xs text-[#FF6B35] font-semibold py-2 bg-[#FF6B35]/10 border border-[#FF6B35]/30 rounded-md hover:bg-[#FF6B35]/20 transition-colors" data-testid="today-program-link">
            Voir mon programme complet →
          </Link>
        </div>
      )}
    </div>
  );
}
